/**
 * Plane resolution utilities for the two-tree routing model (INF-616 / R2.6).
 *
 * The frontend mirrors the API's plane segregation:
 *   /ui/platform/…  — Operations Console  (AP Platform staff)
 *   /ui/partner/…   — Partner Backoffice   (partner agents)
 *
 * Decision D9: all current views are platform-facing; only partner-plane
 * views are placed under /ui/partner/…. New partner-plane features must
 * NOT be added under /ui/platform/.
 */

import { PARTNER_CHAT_ROUTE_PATHS } from '@modules/partner-chat/config';

/** Roles that identify a partner-plane session. */
const PARTNER_PLANE_ROLES = ['partner_agent', 'partner_admin'] as const;

/**
 * Route IDs that belong to the partner plane.
 * All other routes are platform-plane.
 * Add new partner-plane route IDs here as the Partner Backoffice grows.
 */
export const PARTNER_PLANE_ROUTE_IDS = new Set([
  'partner-conversations',
  'partner-conversation-detail',
]);

/**
 * Module IDs that belong to the partner plane.
 * Used for module-level categorization.
 */
export const PARTNER_PLANE_MODULE_IDS = new Set(['partner-chat']);

/**
 * Returns true if the given route (by its TRouteEntry id) belongs to the partner plane.
 */
export function isPartnerPlaneRoute(routeId: string): boolean {
  return PARTNER_PLANE_ROUTE_IDS.has(routeId);
}

/**
 * Unprefixed path prefixes for all partner-plane routes.
 * Derived from the actual route config — single source of truth.
 * Used by the compat catch-all to redirect old unprefixed partner paths to /partner/…
 * instead of /platform/…
 *
 * Add entries here when new partner-plane modules are created.
 */
export const PARTNER_PLANE_UNPREFIXED_PREFIXES: string[] = [
  // partner-chat: /panel/conversations and /panel/conversations/$externalId
  ...Object.values(PARTNER_CHAT_ROUTE_PATHS)
    .map((p) => {
      // Strip the first dynamic segment and everything after it.
      // /panel/conversations/$externalId → /panel/conversations
      // This only strips at a segment boundary to avoid over-broad prefixes.
      const firstDynamic = p.indexOf('/$');
      return firstDynamic === -1 ? p : p.slice(0, firstDynamic);
    })
    .filter((p, i, arr) => arr.indexOf(p) === i), // deduplicate
];

/**
 * Returns true if a bare (unprefixed) path belongs to the partner plane.
 * Used by the compat catch-all redirect to choose /partner vs /platform.
 */
export function isPartnerPlanePath(unprefixedPath: string): boolean {
  return PARTNER_PLANE_UNPREFIXED_PREFIXES.some(
    (prefix) => unprefixedPath === prefix || unprefixedPath.startsWith(`${prefix}/`)
  );
}

/**
 * Given a bare module path (as stored in the module registry, without plane
 * prefix) and a prefixed ROUTE_PATHS map, returns the plane-prefixed path.
 *
 * This is the correct way for components to convert registry paths to
 * navigable paths. Pass ROUTE_PATHS from @modules/core/config/modules.
 *
 * Example:
 *   prefixedPathFor('/dashboard', ROUTE_PATHS)   → '/platform/dashboard'
 *   prefixedPathFor('/panel/conversations', ROUTE_PATHS) → '/partner/panel/conversations'
 *
 * Falls back to barePath if no match is found.
 *
 * Performance: this is O(n) over ROUTE_PATHS values (~50 entries today). Safe to
 * call per-route in sidebar renders. If ROUTE_PATHS grows large, build a
 * Map<barePath, prefixedPath> once with Object.entries and look up in O(1).
 */
export function prefixedPathFor(
  barePath: string,
  routePaths: Record<string, string>
): string {
  const found = Object.values(routePaths).find((prefixed) => {
    const suffix = prefixed.replace(/^\/(?:platform|partner)/, '');
    return suffix === barePath;
  });
  return found ?? barePath;
}

export type Plane = 'platform' | 'partner';

/**
 * Returns the plane for the current session based on its roles.
 * Falls back to 'platform' when no partner role is present.
 */
export function resolvePlane(roles: string[] | undefined): Plane {
  if (!roles?.length) return 'platform';
  const roleSet = new Set(roles);
  if (PARTNER_PLANE_ROLES.some((r) => roleSet.has(r))) return 'partner';
  return 'platform';
}

/** Route path prefix for the platform plane (relative to the /ui basepath). */
export const PLATFORM_PREFIX = '/platform';

/** Route path prefix for the partner plane (relative to the /ui basepath). */
export const PARTNER_PREFIX = '/partner';

/**
 * The router's basepath. Declared here so it exists exactly once — `createRouter({ basepath })`
 * in route-tree.ts reads it, and so does panelUrl() below.
 *
 * It used to be a bare literal in route-tree.ts, which meant every full-page redirect retyped
 * '/ui/...' by hand. Five of them were still pointing at pre-R2.3 paths (INF-850).
 */
export const PANEL_BASEPATH = '/ui';

/**
 * Absolute in-app URL, for the rare case that needs a **full page load** (`window.location`)
 * rather than SPA navigation — e.g. leaving a route whose guard already denied you.
 *
 * Takes an already plane-prefixed path, normally a `ROUTE_PATHS.<key>` value. It does NOT add the
 * plane itself: doing so here would make it ambiguous whether a caller passing '/platform/x' meant
 * that path or wanted it prefixed again.
 *
 *   panelUrl(ROUTE_PATHS.dashboard)                          → '/ui/platform/dashboard'
 *   panelUrl(ROUTE_PATHS.batchProcessing, { batchId: '42' }) → '/ui/platform/batch-processing?batchId=42'
 *
 * For navigation inside the SPA use the router (`navigate` / `<Link>`) with `ROUTE_PATHS` instead —
 * this helper reloads the whole app.
 */
export function panelUrl(prefixedPath: string, search?: Record<string, string>): string {
  const qs = search ? `?${new URLSearchParams(search).toString()}` : '';
  return `${PANEL_BASEPATH}${prefixedPath}${qs}`;
}
